/**
 * AccountOption Component
 * Radio option for a suggested account match
 */
import { cn } from '@/utils/cn';
import ConfidenceBadge from './ConfidenceBadge';

export interface AccountOptionProps {
  id: string;
  accountName: string;
  accountMask?: string;
  accountType: string;
  confidence: number;
  matchReasons: string[];
  selected: boolean;
  onSelect: () => void;
}

export function AccountOption({
  id,
  accountName,
  accountMask,
  accountType,
  confidence,
  matchReasons,
  selected,
  onSelect,
}: AccountOptionProps) {
  // Format account type for display
  const displayAccountType = formatAccountType(accountType);

  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-labelledby={`account-label-${id}`}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors',
        selected
          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
          : 'border-gray-200 hover:bg-gray-50'
      )}
    >
      {/* Radio indicator */}
      <div
        className={cn(
          'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
          selected ? 'border-primary-600' : 'border-gray-300'
        )}
      >
        {selected && <div className="w-2.5 h-2.5 rounded-full bg-primary-600" />}
      </div>

      {/* Account info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span
              id={`account-label-${id}`}
              className={cn(
                'font-medium truncate',
                selected ? 'text-primary-700' : 'text-gray-900'
              )}
            >
              {accountName}
            </span>
            {accountMask && (
              <span className="text-sm text-gray-500 flex-shrink-0">****{accountMask}</span>
            )}
          </div>
          <ConfidenceBadge confidence={confidence} />
        </div>

        <p className="mt-1 text-sm text-gray-500">{displayAccountType}</p>

        {/* Match reasons (shown when selected) */}
        {selected && matchReasons.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500">Match reasons:</p>
            <ul className="mt-1 text-xs text-gray-600 list-disc list-inside">
              {matchReasons.map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Format account type for display
 */
function formatAccountType(type: string): string {
  const typeMap: Record<string, string> = {
    checking: 'Checking Account',
    savings: 'Savings Account',
    credit_card: 'Credit Card',
    credit: 'Credit Card',
    investment: 'Investment Account',
    loan: 'Loan',
    mortgage: 'Mortgage',
    depository: 'Bank Account',
    brokerage: 'Brokerage Account',
  };

  return typeMap[type?.toLowerCase() ?? ''] ?? (type?.charAt(0)?.toUpperCase() ?? '') + (type?.slice(1) ?? '');
}

export default AccountOption;
